import type { FC, ReactNode } from 'react'
import type { Surface } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { LocalTime } from '@/components/generals/local-time'
import { SectionLink } from '@/components/generals/section-link'
import { NAV_ITEMS, QUOTE_HASH } from '@/constants/site'

interface SiteHeaderProps {
    menuOpen: boolean
    onMenuToggle: () => void
    onNavigate: () => void
}

/** Reads the [data-surface] section under the header so the header can flip between light and dark ink. */
const useSurfaceUnderHeader = (path: string): Surface => {
    const [surface, setSurface] = useState<Surface>('dark')

    useEffect(() => {
        let frame = 0

        const probe = () => {
            frame = 0

            const line = 36
            let next: Surface = 'dark'

            for (const section of Array.from(document.querySelectorAll<HTMLElement>('[data-surface]'))) {
                const rect = section.getBoundingClientRect()

                if (rect.top <= line && rect.bottom > line) next = section.dataset.surface as Surface
            }

            setSurface(next)
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(probe)
        }

        probe()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)
        window.addEventListener('tone:refresh', schedule)

        return () => {
            window.cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            window.removeEventListener('tone:refresh', schedule)
        }
    }, [path])

    return surface
}

/** Slides the header away while reading downward and brings it back on the way up. */
const useHideOnScroll = (): boolean => {
    const [hidden, setHidden] = useState(false)

    useEffect(() => {
        let last = window.scrollY
        let frame = 0

        const update = () => {
            frame = 0

            const y = window.scrollY
            const delta = y - last

            if (y < 160) setHidden(false)
            else if (delta > 8) setHidden(true)
            else if (delta < -8) setHidden(false)

            if (Math.abs(delta) > 8) last = y
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        window.addEventListener('scroll', schedule, { passive: true })

        return () => {
            window.cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
        }
    }, [])

    return hidden
}

export const SiteHeader: FC<SiteHeaderProps> = ({ menuOpen, onMenuToggle, onNavigate }): ReactNode => {
    const router = useRouter()
    const detected = useSurfaceUnderHeader(router.asPath)
    const hidden = useHideOnScroll()
    const surface: Surface = menuOpen ? 'dark' : detected
    const dark = surface === 'dark'

    return (
        <header
            data-theme={surface}
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[translate,color] duration-700 ease-out-expo',
                hidden && !menuOpen ? '-translate-y-full' : 'translate-y-0',
                dark ? 'text-paper' : 'text-ink'
            )}
        >
            <div className='flex h-(--header-h) items-center justify-between gap-6 px-(--gutter)'>
                <Link
                    href='/'
                    aria-label="Localboost, back to the home page"
                    onClick={onNavigate}
                    className='shrink-0'
                >
                    <Logo className='text-[20px] md:text-[23px]' />
                </Link>

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-10'>
                        {NAV_ITEMS.map(item => (
                            <li key={item.href}>
                                <SectionLink
                                    hash={item.href}
                                    className='group inline-flex items-start gap-1.5 py-2 text-[0.95rem] font-medium'
                                >
                                    <span className='label pt-0.5 text-[0.58rem] opacity-60'>{item.index}</span>

                                    <span className='relative'>
                                        {item.label}

                                        <span
                                            aria-hidden='true'
                                            className='absolute -bottom-0.5 left-0 h-px w-full origin-right scale-x-0 bg-current transition-transform duration-500 ease-out-expo group-hover:origin-left group-hover:scale-x-100'
                                        />
                                    </span>
                                </SectionLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-4 md:gap-6'>
                    <span className='label hidden items-center gap-2.5 opacity-80 xl:inline-flex'>
                        <span aria-hidden='true' className='size-1.5 rounded-full bg-signal' />
                        Fort Worth
                        <LocalTime />
                    </span>

                    <SectionLink
                        hash={QUOTE_HASH}
                        onClick={onNavigate}
                        className={cn(
                            'group hidden h-11 items-center gap-2.5 px-5 text-sm font-semibold transition-colors duration-300 sm:inline-flex',
                            dark ? 'bg-signal text-ink hover:bg-paper' : 'bg-ink text-paper hover:bg-signal hover:text-ink'
                        )}
                    >
                        Get a free quote
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                    </SectionLink>

                    <button
                        type='button'
                        aria-expanded={menuOpen}
                        aria-controls='site-menu'
                        aria-label={menuOpen ? "Close the site menu" : "Open the site menu"}
                        onClick={onMenuToggle}
                        className='-mr-2 inline-flex h-11 items-center gap-3 px-2 lg:hidden'
                    >
                        <span aria-hidden='true' className='label hidden sm:inline'>{menuOpen ? "Close" : "Menu"}</span>

                        <span aria-hidden='true' className='relative block h-3 w-6'>
                            <span
                                className={cn(
                                    'absolute left-0 h-[2px] w-full bg-current transition-transform duration-500 ease-out-expo',
                                    menuOpen ? 'top-[5px] rotate-45' : 'top-0'
                                )}
                            />

                            <span
                                className={cn(
                                    'absolute left-0 h-[2px] bg-current transition-all duration-500 ease-out-expo',
                                    menuOpen ? 'top-[5px] w-full -rotate-45' : 'top-[10px] w-2/3'
                                )}
                            />
                        </span>
                    </button>
                </div>
            </div>
        </header>
    )
}
